import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, Shield, Clock, Award, CreditCard, Scissors, Package, Ruler } from "lucide-react";
import { useLookups } from "@/context/LookupContext";

const DAYS = [
  { key: "mon", label: "Monday" }, { key: "tue", label: "Tuesday" }, { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" }, { key: "fri", label: "Friday" }, { key: "sat", label: "Saturday" }, { key: "sun", label: "Sunday" },
];

const genId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

function ListManager({ testid, prefix, items, setItems, columns = ["name"], placeholder = { name: "New item" }, description }) {
  const setField = (id, key, value) =>
    setItems(items.map((it) => (it.id === id ? { ...it, [key]: value } : it)));
  const addItem = () => setItems([...items, { id: genId(prefix), ...columns.reduce((a, c) => ({ ...a, [c]: c === "price" ? 0 : "" }), {}) }]);
  const remove = (id) => setItems(items.filter((it) => it.id !== id));

  return (
    <div className="space-y-3" data-testid={testid}>
      {description && <p className="text-sm text-emerald-muted">{description}</p>}
      {items.length === 0 && <div className="text-sm text-emerald-muted italic py-4">No entries yet. Click &ldquo;+ Add&rdquo; below.</div>}
      {items.map((it) => (
        <div key={it.id} className="grid gap-2 items-center bg-emerald-elev/40 border border-gold-subtle rounded-md px-3 py-2" style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr) auto` }}
          data-testid={`${testid}-row-${it.id}`}>
          {columns.map((c) => (
            <Input
              key={c}
              type={c === "price" ? "number" : "text"}
              value={it[c] ?? ""}
              placeholder={placeholder[c] || c}
              onChange={(e) => setField(it.id, c, c === "price" ? e.target.value : e.target.value)}
              className="bg-emerald-elev border-gold-subtle text-white h-9"
              data-testid={`${testid}-${c}-${it.id}`}
            />
          ))}
          <button onClick={() => remove(it.id)} className="text-emerald-muted hover:text-red-300 p-1.5" data-testid={`${testid}-del-${it.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={addItem}
        data-testid={`${testid}-add`}
        className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline"
      >
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
}

export default function Settings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");
  const [membership, setMembership] = useState("");
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const { lookups, setLookups } = useLookups();

  const load = () => api.get("/settings").then((r) => setData(r.data)).catch(() => toast.error("Failed to load settings"));
  useEffect(() => { load(); }, []);

  const loadMemberships = () => api.get("/membership-plans").then((r) => setMembershipPlans(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Failed to load membership plans"));

  const loadServiceCategories = () => api.get("/service-categories").then((r) => setServiceCategories(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Failed to load service categories"));

  const loadProductCategories = () => {
    const cats = lookups?.productCategories || [];
    setProductCategories(cats);
  };

  const onTabChange = (tab) => {
    if (tab === "memberships") loadMemberships();
    if (tab === "service-cats") loadServiceCategories();
    if (tab === "product-cats") loadProductCategories();
  };
  useEffect(() => { loadMemberships(); loadServiceCategories(); loadProductCategories(); }, [lookups]);

  const saveSection = async (section, value) => {
    setSaving(section);
    try {
      const r = await api.post("/settings", { [section]: value });
      setData(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(""); }
  };
  const generatePlanCode = (planName) => {
    const year = new Date().getFullYear();
    const name = (planName || "").toUpperCase();
    if (name === "SILVER") return `SIL${year}`;
    if (name === "GOLD") return `GOLD${year}`;
    if (name === "PLATINUM") return `PLAT${year}`;
    return `${name.slice(0, 4)}${year}`;
  };

  const saveMembers = async (section, value) => {
    setMembership(section);
    try {
      const plans = (value || []).map((item) => ({
        planCode: generatePlanCode(item.name || item.planName),
        planName: item.name || item.planName,
        bonusAmount: Number(item.bonusAmount || 0),
        purchaseAmount: Number(item.purchaseAmount || 0),
        discountPercentage: Number(item.discountPercentage || 0),
      }));
      // Only save new plans (skip already loaded ones that have an id)
      const existingCodes = new Set(membershipPlans.filter((p) => p.id).map((p) => p.planCode));
      const newPlans = plans.filter((p) => !existingCodes.has(p.planCode));
      for (const plan of newPlans) {
        await api.post("/membership-plans", plan);
      }
      toast.success(newPlans.length > 0 ? "Saved" : "No new plans to save");
      loadMemberships();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(""); }
  };

  const saveServiceCategories = async (section, value) => {
    setSaving(section);
    try {
      const newItems = (value || []).filter((item) => !item.id || typeof item.id === "string");
      for (const item of newItems) {
        await api.post("/service-categories", { name: item.name, icon: item.icon, displayOrder: item.displayOrder });
      }
      toast.success(newItems.length > 0 ? "Saved" : "No new categories to save");
      loadServiceCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(""); }
  };

  const saveProductCategories = async (section, value) => {
    setSaving(section);
    try {
      const newItems = (value || []).filter((item) => !item.id || typeof item.id === "string");
      for (const item of newItems) {
        await api.post("/product-categories", { name: item.name, icon: item.icon, displayOrder: item.displayOrder });
      }
      toast.success(newItems.length > 0 ? "Saved" : "No new categories to save");
      // Refresh lookups so other pages get updated categories
      const r = await api.get("/lookups");
      if (r) setLookups(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(""); }
  };

  if (!data) return <div className="text-emerald-muted">Loading settings…</div>;

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="overline mb-2">Configuration</div>
        <h1 className="font-heading text-4xl text-white">Application Settings</h1>
        <p className="text-emerald-muted mt-2 text-sm">Manage roles, hours, memberships, and catalog vocabulary used across the app.</p>
      </div>

      <Tabs defaultValue="roles" className="w-full" onValueChange={onTabChange}>
        <TabsList className="bg-emerald-surface border border-gold-subtle flex-wrap h-auto" data-testid="settings-tabs">
          <TabsTrigger value="roles" data-testid="tab-roles"><Shield className="w-3 h-3 mr-1.5" /> Roles</TabsTrigger>
          <TabsTrigger value="timings" data-testid="tab-timings"><Clock className="w-3 h-3 mr-1.5" /> Store Timings</TabsTrigger>
          <TabsTrigger value="memberships" data-testid="tab-memberships"><Award className="w-3 h-3 mr-1.5" /> Memberships</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments"><CreditCard className="w-3 h-3 mr-1.5" /> Payment Methods</TabsTrigger>
          <TabsTrigger value="service-cats" data-testid="tab-service-cats"><Scissors className="w-3 h-3 mr-1.5" /> Service Categories</TabsTrigger>
          <TabsTrigger value="product-cats" data-testid="tab-product-cats"><Package className="w-3 h-3 mr-1.5" /> Product Categories</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units"><Ruler className="w-3 h-3 mr-1.5" /> Units</TabsTrigger>
        </TabsList>

        {/* Roles */}
        <TabsContent value="roles" className="mt-6">
          <SectionCard title="Roles" onSave={() => saveSection("roles", data.roles)} saving={saving === "roles"}>
            <ListManager
              testid="roles"
              prefix="role"
              items={data.roles || []}
              setItems={(v) => setData({ ...data, roles: v })}
              columns={["name", "description"]}
              placeholder={{ name: "Role name", description: "Description" }}
              description="Create custom labels for your team. Permissions are still governed by the built-in admin / staff / customer role."
            />
          </SectionCard>
        </TabsContent>

        {/* Store Timings */}
        <TabsContent value="timings" className="mt-6">
          <SectionCard title="Store Timings" onSave={() => saveSection("store_timings", data.store_timings)} saving={saving === "store_timings"}>
            <div className="space-y-2" data-testid="timings-editor">
              {DAYS.map((d) => {
                const h = data.store_timings?.[d.key] || { start: "09:00", end: "20:00", closed: false };
                const setDay = (patch) => setData({ ...data, store_timings: { ...data.store_timings, [d.key]: { ...h, ...patch } } });
                return (
                  <div key={d.key} className="grid grid-cols-12 gap-3 items-center bg-emerald-elev/40 border border-gold-subtle rounded-md px-3 py-2" data-testid={`timing-row-${d.key}`}>
                    <div className="col-span-3 text-white text-sm">{d.label}</div>
                    <div className="col-span-3">
                      <Input type="time" value={h.start} disabled={h.closed}
                        onChange={(e) => setDay({ start: e.target.value })}
                        className="bg-emerald-elev border-gold-subtle text-white h-9"
                        data-testid={`timing-${d.key}-start`} />
                    </div>
                    <div className="col-span-1 text-center text-emerald-muted text-xs">to</div>
                    <div className="col-span-3">
                      <Input type="time" value={h.end} disabled={h.closed}
                        onChange={(e) => setDay({ end: e.target.value })}
                        className="bg-emerald-elev border-gold-subtle text-white h-9"
                        data-testid={`timing-${d.key}-end`} />
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <Switch checked={h.closed} onCheckedChange={(v) => setDay({ closed: !!v })} data-testid={`timing-${d.key}-closed`} />
                      <span className="text-xs text-emerald-muted">Closed</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </TabsContent>

        {/* Memberships */}
        <TabsContent value="memberships" className="mt-6">
          <SectionCard title="Memberships" onSave={() => saveMembers("memberships", membershipPlans)} saving={saving === "memberships"}>
            <ListManager
              testid="memberships"
              prefix="mem"
              items={membershipPlans}
              setItems={setMembershipPlans}
              columns={["planName", "purchaseAmount", "bonusAmount", "discountPercentage"]}
              placeholder={{ name: "Membership Name", purchaseAmount: "Purchased Amount", bonusAmount: "Bonus Amount", discountPercentage: "Discount %" }}
              description="Define membership tiers. planCode is auto-generated (SIL2026, GOLD2026, PLAT2026)."
            />
          </SectionCard>
        </TabsContent>

        {/* Payment Methods */}
        <TabsContent value="payments" className="mt-6">
          <SectionCard title="Payment Methods" onSave={() => saveSection("payment_methods", data.payment_methods)} saving={saving === "payment_methods"}>
            <ListManager
              testid="payments"
              prefix="pm"
              items={data.payment_methods || []}
              setItems={(v) => setData({ ...data, payment_methods: v })}
              columns={["name"]}
              placeholder={{ name: "e.g. Cash, Card, UPI…" }}
              description="Payment methods available at checkout and Quick Sale."
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="service-cats" className="mt-6">
          <SectionCard title="Service Categories" onSave={() => saveServiceCategories("service_categories", serviceCategories)} saving={saving === "service_categories"}>
            <ListManager
              testid="service-cats"
              prefix="sc"
              items={serviceCategories}
              setItems={setServiceCategories}
              columns={["name", "icon", "displayOrder"]}
              placeholder={{ name: "Category Name", icon: "icon to display", displayOrder: "display order" }}
              description="Used to group services and color-code them across Calendar and Dashboard."
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="product-cats" className="mt-6">
          <SectionCard title="Product Categories" onSave={() => saveProductCategories("product_categories", productCategories)} saving={saving === "product_categories"}>
            <ListManager
              testid="product-cats"
              prefix="pc"
              items={productCategories}
              setItems={setProductCategories}
              columns={["name", "icon", "displayOrder"]}
              placeholder={{ name: "e.g. Hair Care, Skin Care…", icon: "icon to display", displayOrder: "display order" }}
              description="Used to group inventory items."
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="units" className="mt-6">
          <SectionCard title="Units" onSave={() => saveSection("units", data.units)} saving={saving === "units"}>
            <ListManager
              testid="units"
              prefix="u"
              items={data.units || []}
              setItems={(v) => setData({ ...data, units: v })}
              columns={["name"]}
              placeholder={{ name: "e.g. ml, gm, pcs…" }}
              description="Measurement units for inventory items."
            />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SectionCard({ title, children, onSave, saving }) {
  return (
    <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-2xl text-white">{title}</h3>
        <button onClick={onSave} disabled={saving}
          className="gold-button inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm disabled:opacity-60"
          data-testid={`save-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
      <Label className="sr-only">{title}</Label>
      {children}
    </div>
  );
}
